import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Timer, ListChecks } from 'lucide-react';

import { Btn, Kbd, Wheel, WordRushLockup, STAGE } from '../components/wr';
import { useAuth } from '../context/AuthContext';
import { useWebSocket, useGame } from '../context/GameContext';
import { useHotkeys } from '../hooks/useHotkeys';
import { useSurface } from '../hooks/useSurface';
import { todayLetter } from '../services/dailyChallengeService';

/** Handed from the landing to Home across the post-sign-in navigation. */
export const START_TABLE_INTENT = 'wr.startTableOnArrival';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
/** Matches the wheel's own settle: 400 accel + 1400 cruise + 700 settle. */
const SPIN_MS = 2300;

/**
 * The logged-out landing.
 *
 * What it replaces: a 1.8s splash, then a 480px login form. That was the
 * public preview's entire first impression — a wall, on a canvas that could
 * have shown the game.
 *
 * THE LANDING IS THE TUTORIAL. The hero is the real `wr/Wheel`, idle-drifting
 * at 1rpm. You press Space (or click it) and it spins to a real letter, and
 * the caption underneath says:
 *
 *     "S — name, place, animal, thing. That's the game."
 *
 * That one line, delivered by an object you just made move yourself, is the
 * whole rules explanation. Nobody reads a tutorial; everybody spins a wheel.
 *
 * And there is NO SIGN-IN WALL. "Start a table" provisions a guest and drops
 * you straight into a lobby. The wall was never protecting anything.
 */
export default function LandingScreen() {
  const navigate = useNavigate();
  const surface = useSurface();
  const { signInAsGuest, currentUser } = useAuth();
  const { createRoom } = useWebSocket();
  const { state } = useGame();

  /**
   * "Start a table — free, no app" must land you in a lobby. It didn't: it
   * landed you on the sign-in wall this page exists to abolish. Every time.
   *
   * Two races stacked on each other. signInAsGuest() resolves when Firebase
   * says "authenticated", but `state.currentPlayer` is populated by an effect
   * downstream of that — so createRoom() threw "No current player". And the
   * moment auth lands, App routes an authenticated visitor off `/` to `/home`,
   * which UNMOUNTS this screen — so waiting here just waits to be killed.
   *
   * Don't fight the navigation: hand the intent across it. Mint the guest, park
   * the intent, and let Home — which already creates rooms reliably from its
   * own hero card — pick it up on arrival.
   */
  const startTable = async () => {
    if (busy) return;
    setBusy('table');
    try {
      sessionStorage.setItem(START_TABLE_INTENT, '1');
      if (!currentUser) await signInAsGuest();
      // Already signed in? Then no navigation is coming to carry us; go.
      navigate('/home');
    } catch (e) {
      console.error('Could not start a table', e);
      sessionStorage.removeItem(START_TABLE_INTENT);
      setBusy(null);
      navigate('/login');
    }
  };

  const [letter, setLetter] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [busy, setBusy] = useState<null | 'table' | 'daily'>(null);
  const [paused, setPaused] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const daily = todayLetter();
  const wheelPx =
    surface === 'desktop' ? STAGE.WHEEL_PX_LG * 0.875 /* 420 */
      : surface === 'tablet' ? 320
        : 260;

  const spin = useCallback(() => {
    if (spinning) return;
    setSpinning(true);
    setLetter(ALPHABET[Math.floor(Math.random() * ALPHABET.length)]);
    timer.current = setTimeout(() => setSpinning(false), SPIN_MS);
  }, [spinning]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Space spins the wheel. Not gated on desktop: a phone visitor with a
  // bluetooth keyboard is welcome to it, and it costs nothing.
  useHotkeys([{ keys: [' ', 'Spacebar'], handler: spin }]);

  const playDaily = async () => {
    if (busy) return;
    setBusy('daily');
    try {
      if (!currentUser) await signInAsGuest();
      navigate('/daily');
    } catch (e) {
      console.error('Guest sign-in failed', e);
      setBusy(null);
      navigate('/login');
    }
  };

  const caption = spinning
    ? 'Spinning…'
    : letter
      ? `${letter} — name, place, animal, thing. That's the game.`
      : surface === 'desktop'
        ? 'Press Space to spin the wheel.'
        : 'Tap the wheel to spin it.';

  return (
    <div className="app-shell">
      <div className="screen-pad wr-page wr-page--max" style={{ paddingTop: 20 }}>
        <header className="wr-between" style={{ paddingBottom: 8 }}>
          <WordRushLockup size={18} showByline={false} />
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              minHeight: 44,
              padding: '0 12px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              fontFamily: 'var(--ui)',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Sign in
          </button>
        </header>

        <div className="wr-landing wr-col gap-6" style={{ paddingTop: 8 }}>
          {/* ---- The hero. It is not a picture of the game; it IS the game. */}
          <div className="wr-col wr-center gap-4" style={{ order: surface === 'desktop' ? 2 : 1 }}>
            <button
              type="button"
              onClick={spin}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              aria-label={letter ? `Wheel — landed on ${letter}. Spin again` : 'Spin the letter wheel'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 8,
                cursor: spinning ? 'default' : 'pointer',
                borderRadius: '50%',
                lineHeight: 0,
              }}
            >
              <Wheel size={wheelPx} selected={letter} spinning={spinning} idle={!paused} />
            </button>

            {/* Fixed-height caption lane. It cross-fades; it never reflows the
                hero — the wheel would visibly jump on every spin. */}
            <div
              style={{ height: 28, position: 'relative', width: '100%', maxWidth: 420 }}
              aria-live="polite"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={caption}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="wr-center"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    fontFamily: 'var(--display)',
                    fontWeight: 700,
                    fontSize: 17,
                    letterSpacing: '-0.01em',
                    color: letter && !spinning ? 'var(--ink)' : 'var(--ink-3)',
                    textAlign: 'center',
                  }}
                >
                  {caption}
                </motion.div>
              </AnimatePresence>
            </div>
            {surface === 'desktop' && (
              <div className="wr-row gap-2 wr-center" style={{ color: 'var(--ink-3)', fontSize: 13 }}>
                <Kbd>Space</Kbd> <span>to spin</span>
              </div>
            )}
          </div>

          {/* ---- The words. Short, and true. */}
          <div className="wr-col gap-4" style={{ order: surface === 'desktop' ? 1 : 2 }}>
            <div className="wr-row gap-2" style={{ alignItems: 'center' }}>
              <Sparkles size={14} color="var(--accent)" />
              <span className="eyebrow accent">The family word game</span>
            </div>
            <h1
              className="h1 lead-h1"
              style={{ fontSize: surface === 'phone' ? 34 : 44, lineHeight: 1.05 }}
            >
              Name · Place · Animal · Thing
            </h1>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.5,
                color: 'var(--ink-3)',
                margin: 0,
                maxWidth: 460,
              }}
            >
              One letter, one minute, everyone at the table. Then the tally — where
              you find out your brother wrote the same word as you.
            </p>

            <div className="wr-col gap-2" style={{ maxWidth: 420, width: '100%' }}>
              <Btn
                kind="primary"
                size="xl"
                full
                icon="Play"
                loading={busy === 'table'}
                onClick={startTable}
              >
                Start a table — free, no app
              </Btn>
              <button
                type="button"
                onClick={playDaily}
                disabled={busy === 'daily'}
                className="card wr-row gap-3"
                style={{
                  width: '100%',
                  minHeight: 56,
                  padding: '10px 14px',
                  border: 'none',
                  cursor: 'pointer',
                  alignItems: 'center',
                  textAlign: 'left',
                }}
              >
                <span
                  className="wr-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 11,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent)',
                    fontFamily: 'var(--display)',
                    fontWeight: 800,
                    fontSize: 19,
                    flexShrink: 0,
                  }}
                >
                  {/* The REAL letter, the one /daily will actually serve. */}
                  {daily}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: 'var(--display)', fontWeight: 700, fontSize: 16 }}>
                    Play today's Daily · {daily}
                  </span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--ink-3)', marginTop: 1 }}>
                    No clock. Four words. Come back tomorrow.
                  </span>
                </span>
                <ArrowRight size={18} color="var(--ink-3)" style={{ flexShrink: 0 }} />
              </button>
            </div>

            {/* No player count. We don't have an honest one to show, and a
                fabricated "12,483 playing now" is the fastest way to teach a
                family that nothing on this page is true. Nothing beats a lie. */}
          </div>
        </div>

        {/* ---- How a round works. Three steps, no video, no carousel. */}
        <div className="wr-landing-steps wr-col gap-2 mt-6" style={{ paddingBottom: 32 }}>
          {[
            { n: '1', icon: Sparkles, t: 'The wheel picks a letter', s: 'Everyone gets the same one.' },
            { n: '2', icon: Timer, t: '60 seconds, four words', s: 'A name, a place, an animal, a thing.' },
            { n: '3', icon: ListChecks, t: 'The tally', s: 'Unique 10. Same word as someone? 5 each.' },
          ].map(step => {
            const Icon = step.icon;
            return (
              <div key={step.n} className="card wr-row gap-3" style={{ alignItems: 'flex-start', padding: 16 }}>
                <span
                  className="wr-center"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--bg-2)',
                    color: 'var(--ink-3)',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} strokeWidth={2.3} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15 }}>
                    {step.t}
                  </span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
                    {step.s}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
